import { v4 as uuid } from "uuid";
import { putToSignedUrl, storageKeyFromUrl } from "./storage-upload";

export const MAX_CLIPBOARD_IMAGE_BYTES = 10 * 1024 * 1024;

export type GetSignedUrlFn = (key: string) => Promise<string>;

export function extensionFromImageFile(file: File): string {
  const fromType = file.type.split("/")[1]?.toLowerCase();
  if (!fromType) return "png";
  if (fromType === "jpeg") return "jpg";
  return fromType.replace(/[^a-z0-9]/gi, "") || "png";
}

/** Recoge imágenes del portapapeles (files + items) sin duplicados. */
export function collectClipboardImageFiles(data: DataTransfer): File[] {
  const files: File[] = [];
  const seen = new Set<string>();

  const add = (file: File | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    const key = `${file.name}-${file.size}-${file.type}-${file.lastModified}`;
    if (seen.has(key)) return;
    seen.add(key);
    files.push(file);
  };

  // En paste, Chrome/Safari exponen la misma imagen en `files` e `items`.
  // Usar solo una fuente para no subirla dos veces.
  if (data.files?.length) {
    Array.from(data.files).forEach(add);
    return files;
  }

  if (data.items?.length) {
    for (let index = 0; index < data.items.length; index++) {
      const item = data.items[index];
      if (item.kind === "file" && item.type.startsWith("image/")) {
        add(item.getAsFile());
      }
    }
  }

  return files;
}

export function extensionFromUploadFile(file: File): string {
  const nameExt = file.name.split(".").pop()?.toLowerCase();
  if (nameExt && /^[a-z0-9]+$/i.test(nameExt)) return nameExt;
  if (file.type.startsWith("image/")) return extensionFromImageFile(file);
  const fromType = file.type.split("/")[1]?.toLowerCase();
  if (!fromType) return "bin";
  return fromType.replace(/[^a-z0-9]/gi, "") || "bin";
}

/** Sube cualquier archivo vía signed URL (misma ruta que imágenes del clipboard). */
export async function uploadFileWithSignedUrl(
  file: File,
  getSignedUrl: GetSignedUrlFn,
  options?: { maxBytes?: number }
): Promise<string> {
  const maxBytes = options?.maxBytes ?? MAX_CLIPBOARD_IMAGE_BYTES;

  if (file.size > maxBytes) {
    throw new Error("IMAGE_TOO_LARGE");
  }

  const key = `${uuid()}.${extensionFromUploadFile(file)}`;
  const signedPutUrl = await getSignedUrl(key);
  const contentType = file.type || "application/octet-stream";

  try {
    return await putToSignedUrl(signedPutUrl, file, contentType);
  } catch {
    throw new Error("UPLOAD_FAILED");
  }
}

export async function uploadClipboardImageFile(
  file: File,
  getSignedUrl: GetSignedUrlFn,
  options?: { maxBytes?: number }
): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("NOT_IMAGE");
  }

  return uploadFileWithSignedUrl(file, getSignedUrl, options);
}

/**
 * Extrae la key usada en `getSignedUrl` a partir de la URL pública del blob.
 *
 * Devolvía sólo el último segmento, pero el backend compone la key como
 * `${vendorId}/starter/${uuid()}/${key}`, así que el borrado apuntaba a un blob
 * que no existe y fallaba en silencio.
 */
export function extractStorageKeyFromPublicUrl(publicUrl: string): string | null {
  return storageKeyFromUrl(publicUrl);
}

/**
 * Intenta borrar el blob. `getSignedUrl` hoy emite SAS solo para PUT;
 * si el backend no habilita DELETE, devuelve `false` sin lanzar error.
 */
export async function deleteClipboardImageFromStorage(
  publicUrl: string,
  getSignedUrl: GetSignedUrlFn
): Promise<boolean> {
  const key = extractStorageKeyFromPublicUrl(publicUrl);
  if (!key) return false;

  try {
    const signedDeleteUrl = await getSignedUrl(key);
    const response = await fetch(signedDeleteUrl, {
      method: "DELETE",
      headers: { "x-ms-version": "2020-10-02" },
    });

    return response.ok || response.status === 404;
  } catch {
    return false;
  }
}
